// ═══════════════════════════════════════════════════════════════
// Business Owner Portal — Facilities
// CRUD for own org's facilities only, orgId auto-set
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo, useEffect } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Switch,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid,
} from '../../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { useMyOrg } from '../../../hooks/useMyOrg';
import { useMyFacilities } from '../../../hooks/useMyOrgData';
import { useMainStreetStore } from '../../../store/mainstreetStore';
import type { MSFacility } from '../../../store/mainstreetStore';
import { BUILDING_TYPES } from '../constants';

const BUILDING_SELECT = BUILDING_TYPES.map(b => ({ value: b.id, label: b.label }));

const EMPTY: Partial<MSFacility> = {
  orgId: '', name: '', type: '', address: '', city: '', state: '', zip: '',
  buildingType: '', sqft: '', stories: '', occDay: '', occNight: '',
  hasGen: 'false', genDetails: '', hasHazmat: 'false', hazmatDetails: '',
  notes: '', coordinates: '',
};

export default function PortalFacilities() {
  const myOrg = useMyOrg();
  const myFacilities = useMyFacilities();
  const loadFacilities = useMainStreetStore(s => s.loadFacilities);
  const loadingFacilities = useMainStreetStore(s => s.loadingFacilities);
  const createFacility = useMainStreetStore(s => s.createFacility);
  const updateFacility = useMainStreetStore(s => s.updateFacility);
  const deleteFacility = useMainStreetStore(s => s.deleteFacility);

  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MSFacility | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MSFacility | null>(null);
  const [form, setForm] = useState<Partial<MSFacility>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    let list = myFacilities;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(f =>
        (f.name || '').toLowerCase().includes(q) ||
        (f.address || '').toLowerCase().includes(q) ||
        (f.city || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [myFacilities, search]);

  useEffect(() => { setPage(1); }, [filtered.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setField = (key: keyof MSFacility, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY, orgId: myOrg ? String(myOrg.id) : '' });
    setModalOpen(true);
  };

  const openEdit = (fac: MSFacility) => {
    setEditTarget(fac);
    setForm({ ...fac });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      notifications.show({ message: 'Facility name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await updateFacility(editTarget.id, form);
        notifications.show({ message: 'Facility updated', color: 'green' });
      } else {
        await createFacility({ ...form, orgId: myOrg ? String(myOrg.id) : '' });
        notifications.show({ message: 'Facility created', color: 'green' });
      }
      setModalOpen(false);
      setEditTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteFacility(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.name} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">My Facilities</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingFacilities} onClick={() => loadFacilities()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Facility</Button>
        </Group>
      </Group>

      <TextInput placeholder="Search name, address, city..."
        leftSection={<IconSearch size={14} />} value={search}
        onChange={e => setSearch(e.target.value)} size="xs" mb="md"
        style={{ maxWidth: 400 }} />

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} facilit{filtered.length !== 1 ? 'ies' : 'y'}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Address</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Building</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Gen</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Hazmat</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loadingFacilities ? 'Loading...' : 'No facilities found'}
                  </td>
                </tr>
              )}
              {pageRows.map(fac => (
                <tr key={fac.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{fac.name || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{fac.type || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{[fac.address, fac.city, fac.state].filter(Boolean).join(', ') || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {BUILDING_TYPES.find(b => b.id === fac.buildingType)?.label ?? fac.buildingType ?? '-'}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <Badge size="xs" color={fac.hasGen === 'true' ? 'green' : 'gray'}>{fac.hasGen === 'true' ? 'Yes' : 'No'}</Badge>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <Badge size="xs" color={fac.hasHazmat === 'true' ? 'red' : 'gray'}>{fac.hasHazmat === 'true' ? 'Yes' : 'No'}</Badge>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(fac)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(fac)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create / Edit Modal — no org selector, auto-set */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Facility' : 'Add Facility'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Name" required value={form.name ?? ''}
              onChange={e => setField('name', e.target.value)} />
            <TextInput label="Type" value={form.type ?? ''}
              onChange={e => setField('type', e.target.value)} />
            <Select label="Building Type" data={BUILDING_SELECT} value={form.buildingType || null}
              onChange={v => setField('buildingType', v ?? '')} clearable />
            <TextInput label="Address" value={form.address ?? ''}
              onChange={e => setField('address', e.target.value)} />
            <TextInput label="City" value={form.city ?? ''}
              onChange={e => setField('city', e.target.value)} />
            <TextInput label="State" value={form.state ?? ''}
              onChange={e => setField('state', e.target.value)} />
            <TextInput label="ZIP" value={form.zip ?? ''}
              onChange={e => setField('zip', e.target.value)} />
            <TextInput label="Sq Ft" value={form.sqft ?? ''}
              onChange={e => setField('sqft', e.target.value)} />
            <TextInput label="Stories" value={form.stories ?? ''}
              onChange={e => setField('stories', e.target.value)} />
            <TextInput label="Occ. Day" value={form.occDay ?? ''}
              onChange={e => setField('occDay', e.target.value)} />
            <TextInput label="Occ. Night" value={form.occNight ?? ''}
              onChange={e => setField('occNight', e.target.value)} />
          </SimpleGrid>

          <Group>
            <Switch label="Has Generator" checked={form.hasGen === 'true'}
              onChange={e => setField('hasGen', e.currentTarget.checked ? 'true' : 'false')} />
            <Switch label="Has Hazmat" checked={form.hasHazmat === 'true'}
              onChange={e => setField('hasHazmat', e.currentTarget.checked ? 'true' : 'false')} />
          </Group>

          {form.hasGen === 'true' && (
            <TextInput label="Generator Details" value={form.genDetails ?? ''}
              onChange={e => setField('genDetails', e.target.value)} />
          )}
          {form.hasHazmat === 'true' && (
            <TextInput label="Hazmat Details" value={form.hazmatDetails ?? ''}
              onChange={e => setField('hazmatDetails', e.target.value)} />
          )}

          <TextInput label="Coordinates" value={form.coordinates ?? ''}
            onChange={e => setField('coordinates', e.target.value)}
            placeholder="lat, lng" />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Facility'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Facility" centered size="sm">
        <Stack>
          <Text size="sm">Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
